/**
 * Sample data for the Signal System screens, ported from the `renderVals()`
 * method of the design project's DCLogic script.
 *
 * These are presentation fixtures, not app data — the screens are being built
 * as a design reference first, so they render from this file rather than from
 * Apollo. Swapping a screen onto real queries later means replacing the import,
 * not rewriting the markup.
 */
import {
  bars,
  lightBars,
  WaveformBar,
} from '@/common/components/signal/waveformBars';
import { AvatarPerson } from '@/common/components/signal/InitialsAvatar';
import {
  signalAvatarTones,
  signalAvatarTonesLight,
  signalColors,
  signalColorsOnLight,
  signalDawLogos,
  signalStatus,
  SignalStatusKey,
  signalText,
} from '@/theme/foundations/tokens';

const person = (initials: string, tone: keyof typeof signalAvatarTones) => ({
  initials,
  ...signalAvatarTones[tone],
});

const MK = person('MK', 'accent');
const JD = person('JD', 'cyan');
const AR = person('AR', 'green');
const more = (n: number) => person(`+${n}`, 'neutral');

// ---------------------------------------------------------------------------
// 01 Projects grid
// ---------------------------------------------------------------------------

export interface ProjectFixture {
  name: string;
  daw: string;
  logo: string;
  meta: string;
  updated: string;
  status: SignalStatusKey;
  statusLabel: string;
  people: AvatarPerson[];
  wave: WaveformBar[];
}

export const projects: ProjectFixture[] = [
  {
    name: 'Nightdrive',
    daw: 'Ableton Live',
    logo: signalDawLogos.Ableton,
    meta: 'ABLETON · 1.84 GB · v14',
    updated: '4 min ago',
    status: 'synced',
    statusLabel: 'SYNCED',
    people: [MK, JD, more(2)],
  },
  {
    name: 'Halcyon',
    daw: 'FL Studio',
    logo: signalDawLogos.FLStudio,
    meta: 'FL STUDIO · 986 MB · v7',
    updated: '22 min ago',
    status: 'conflict',
    statusLabel: 'CONFLICT',
    people: [MK, AR],
  },
  {
    name: 'Coastline (stems)',
    daw: 'Stems',
    logo: signalDawLogos.LogicPro,
    meta: 'LOGIC PRO · 704 MB · v3',
    updated: '1 h ago',
    status: 'syncing',
    statusLabel: 'SYNCING',
    people: [JD],
  },
  {
    name: 'Slow Static',
    daw: 'Pro Tools',
    logo: signalDawLogos.ProTools,
    meta: 'PRO TOOLS · 3.10 GB · v21',
    updated: 'Yesterday',
    status: 'behind',
    statusLabel: 'BEHIND 2',
    people: [MK, AR, more(4)],
  },
  {
    name: 'Ferrite',
    daw: 'Cubase',
    logo: signalDawLogos.Cubase,
    meta: 'CUBASE · 1.22 GB · v9',
    updated: '2 days ago',
    status: 'synced',
    statusLabel: 'SYNCED',
    people: [AR],
  },
  {
    name: 'Room Tone 04',
    daw: 'Reaper',
    logo: signalDawLogos.Reaper,
    meta: 'REAPER · 412 MB · v2',
    updated: '5 days ago',
    status: 'synced',
    statusLabel: 'SYNCED',
    people: [MK, JD],
  },
].map((p, i) => ({ ...p, wave: bars(44, i * 9 + 2, 44) })) as ProjectFixture[];

// ---------------------------------------------------------------------------
// 02 Project detail
// ---------------------------------------------------------------------------

export interface VersionFixture {
  tag: string;
  title: string;
  note: string;
  meta: string;
  dot: string;
  ring: string;
}

export const versions: VersionFixture[] = [
  {
    tag: 'v14',
    title: 'Vocal comp + new bridge',
    note: 'Swapped the 2nd verse take, retuned bridge stack, bounced a rough master.',
    meta: 'MARA KADE · 4 MIN AGO · +182 MB',
    dot: signalColors.accent,
    ring: 'rgba(255,106,31,0.18)',
  },
  {
    tag: 'v13',
    title: 'Drum bus rebuild',
    note: 'Parallel comp on the drum bus, replaced the clap layer.',
    meta: 'JONAH DEY · 3 H AGO · +41 MB',
    dot: signalColors.cyan,
    ring: 'rgba(85,200,232,0.16)',
  },
  {
    tag: 'v12',
    title: 'Arrangement lock',
    note: 'Structure signed off with the label. Tagged as a milestone.',
    meta: 'MARA KADE · YESTERDAY · +12 MB',
    dot: signalColors.green,
    ring: 'rgba(62,207,142,0.16)',
  },
  {
    tag: 'v11',
    title: 'Sample cleanup',
    note: 'Collected all and saved; removed 118 unused samples.',
    meta: 'MARA KADE · 3 DAYS AGO · −604 MB',
    dot: signalText.muted,
    ring: 'rgba(255,255,255,0.06)',
  },
  {
    tag: 'v10',
    title: 'Session imported',
    note: 'First upload from the desktop agent.',
    meta: 'MARA KADE · 12 DAYS AGO · 1.9 GB',
    dot: signalText.muted,
    ring: 'rgba(255,255,255,0.06)',
  },
];

/** 120-bar player waveform, played to 43%. */
export const playerWave = bars(120, 5, 78, 0.43);

// ---------------------------------------------------------------------------
// 03 Upload flow
// ---------------------------------------------------------------------------

export interface DetectedFixture {
  name: string;
  daw: string;
  logo: string;
  meta: string;
  checked: boolean;
  tag: string;
  tagStatus: SignalStatusKey;
}

export const detected: DetectedFixture[] = [
  {
    name: 'Nightdrive Project',
    daw: 'Ableton Live',
    logo: signalDawLogos.Ableton,
    meta: '.ALS · 412 FILES · 1.84 GB · MODIFIED TODAY',
    checked: true,
    tag: 'NEW',
    tagStatus: 'syncing',
  },
  {
    name: 'Halcyon',
    daw: 'FL Studio',
    logo: signalDawLogos.FLStudio,
    meta: '.FLP · 168 FILES · 470 MB · MODIFIED 2 DAYS AGO',
    checked: true,
    tag: 'UPDATE → v8',
    tagStatus: 'stems',
  },
  {
    name: 'Bounces (loose audio)',
    daw: 'Stems',
    logo: signalDawLogos.StudioOne,
    meta: 'NO SESSION FILE · 26 WAV · 630 MB',
    checked: false,
    tag: 'STEMS',
    tagStatus: 'neutral',
  },
];

/** 18-bar accent waveform used on the stems choice card and the auth handoff panel. */
export const stemsWave = bars(18, 13, 28);

// ---------------------------------------------------------------------------
// 04 Transfer sidebar
// ---------------------------------------------------------------------------

export interface TransferFixture {
  name: string;
  pct: number;
  sub: string;
  eta: string;
}

export const uploads: TransferFixture[] = [
  {
    name: 'Nightdrive Project.als',
    pct: 64,
    sub: '11.8 MB / 18.4 MB',
    eta: '4 s left',
  },
  {
    name: 'Renders/rough master v14.wav',
    pct: 22,
    sub: '13.7 MB / 62.4 MB',
    eta: '28 s left',
  },
];

export const download: TransferFixture = {
  name: 'Coastline stems (14 files)',
  pct: 31,
  sub: '218 MB / 704 MB',
  eta: '2 min left',
};

// ---------------------------------------------------------------------------
// 05 Collaboration board
// ---------------------------------------------------------------------------

export interface BoardCardFixture {
  name: string;
  daw: string;
  logo: string;
  meta: string;
  tag: string;
  tagStatus: SignalStatusKey;
  avatar: AvatarPerson;
  wave: WaveformBar[];
  /** Renders the lifted, tilted drag state. */
  dragging?: boolean;
}

export interface BoardColumnFixture {
  name: string;
  dot: string;
  count: number;
  cards: BoardCardFixture[];
}

const boardCard = (
  name: string,
  daw: string,
  logo: string,
  meta: string,
  tag: string,
  tagStatus: SignalStatusKey,
  avatar: AvatarPerson,
  seed: number,
  dragging = false
): BoardCardFixture => ({
  name,
  daw,
  logo,
  meta,
  tag,
  tagStatus,
  avatar,
  wave: bars(24, seed, 18),
  dragging,
});

export const columns: BoardColumnFixture[] = [
  {
    name: 'WRITING',
    dot: signalText.muted,
    count: 2,
    cards: [
      boardCard(
        'Ferrite',
        'Cubase',
        signalDawLogos.Cubase,
        'CUBASE · v9',
        'IDEA',
        'neutral',
        AR,
        4
      ),
      boardCard(
        'Room Tone 04',
        'Reaper',
        signalDawLogos.Reaper,
        'REAPER · v2',
        'SKETCH',
        'neutral',
        MK,
        11
      ),
    ],
  },
  {
    name: 'RECORDING',
    dot: signalColors.cyan,
    count: 2,
    cards: [
      boardCard(
        'Coastline',
        'Logic Pro',
        signalDawLogos.LogicPro,
        'LOGIC · v3',
        'VOX TODAY',
        'stems',
        JD,
        19
      ),
      boardCard(
        'Halcyon',
        'FL Studio',
        signalDawLogos.FLStudio,
        'FL STUDIO · v7',
        'CONFLICT',
        'conflict',
        MK,
        27
      ),
    ],
  },
  {
    name: 'MIXING',
    dot: signalColors.accent,
    count: 2,
    cards: [
      boardCard(
        'Nightdrive',
        'Ableton Live',
        signalDawLogos.Ableton,
        'ABLETON · v14',
        'DRAGGING',
        'syncing',
        MK,
        33,
        true
      ),
      boardCard(
        'Slow Static',
        'Pro Tools',
        signalDawLogos.ProTools,
        'PRO TOOLS · v21',
        'REV 2',
        'neutral',
        AR,
        41
      ),
    ],
  },
  {
    name: 'MASTERED',
    dot: signalColors.green,
    count: 1,
    cards: [
      boardCard(
        'Paperweight',
        'Studio One',
        signalDawLogos.StudioOne,
        'STUDIO ONE · v18',
        'SENT TO LABEL',
        'synced',
        JD,
        47
      ),
    ],
  },
];

/**
 * The board as the landing page shows it. Same four columns, but the card
 * frozen mid-drag is stood back up: a marketing screenshot shouldn't advertise
 * a tilted card the reader can't finish dropping, so it reports its mix version
 * instead of the drag state.
 */
export const marketingColumns: BoardColumnFixture[] = columns.map((column) => ({
  ...column,
  cards: column.cards.map((card) =>
    card.dragging
      ? { ...card, tag: 'MIX v14', tagStatus: 'syncing', dragging: false }
      : card
  ),
}));

// ---------------------------------------------------------------------------
// 07 Boards list
// ---------------------------------------------------------------------------

export interface BoardFixture {
  name: string;
  meta: string;
  updated: string;
  people: AvatarPerson[];
  accent: string;
  wave: WaveformBar[];
}

export const boards: BoardFixture[] = [
  {
    name: 'Autocannon — LP1',
    meta: '9 PROJECTS · 4 COLUMNS',
    updated: 'Updated 4 min ago',
    people: [MK, JD, AR],
    accent: signalColors.accent,
  },
  {
    name: 'Singles 2026',
    meta: '5 PROJECTS · 3 COLUMNS',
    updated: 'Updated yesterday',
    people: [JD, MK],
    accent: signalColors.cyan,
  },
  {
    name: 'Sync / Licensing queue',
    meta: '12 PROJECTS · 5 COLUMNS',
    updated: 'Updated 3 days ago',
    people: [AR, more(3)],
    accent: signalColors.green,
  },
  {
    name: 'Remix pack — Ferrite',
    meta: '4 PROJECTS · 3 COLUMNS',
    updated: 'Updated last week',
    people: [MK],
    accent: signalText.muted,
  },
].map((b, i) => ({ ...b, wave: bars(40, i * 13 + 6, 26) }));

// ---------------------------------------------------------------------------
// 09 Stems pack detail
// ---------------------------------------------------------------------------

export type StemState = 'playing' | 'solo' | 'muted' | null;

export interface StemFixture {
  name: string;
  meta: string;
  state: StemState;
  wave: WaveformBar[];
  /** Row opacity — muted stems recede. */
  dim: number;
  badge: string;
  badgeFg: string;
  badgeBg: string;
  rowBg: string;
}

const stem = (
  name: string,
  meta: string,
  seed: number,
  state: StemState
): StemFixture => ({
  name,
  meta,
  state,
  wave: bars(90, seed, 30, state === 'playing' ? 0.43 : undefined),
  dim: state === 'muted' ? 0.34 : 1,
  badge: state === 'solo' ? 'SOLO' : state === 'muted' ? 'MUTED' : '',
  badgeFg: state === 'solo' ? signalColors.accentBright : signalText.muted,
  badgeBg:
    state === 'solo'
      ? 'rgba(255,106,31,0.16)'
      : state === 'muted'
        ? 'rgba(255,255,255,0.06)'
        : 'transparent',
  rowBg: state === 'playing' ? 'rgba(255,106,31,0.07)' : 'transparent',
});

export const stems: StemFixture[] = [
  stem('01 Kick.wav', '48 kHz / 24-bit · 41.2 MB', 3, 'playing'),
  stem('02 Sub Bass.wav', '48 kHz / 24-bit · 38.8 MB', 12, null),
  stem('03 Drums Bus.wav', '48 kHz / 24-bit · 52.6 MB', 21, 'solo'),
  stem('04 Synth Pad.wav', '48 kHz / 24-bit · 44.1 MB', 30, null),
  stem('05 Lead Vox.wav', '48 kHz / 24-bit · 61.9 MB', 39, null),
  stem('06 Vox Doubles.wav', '48 kHz / 24-bit · 57.3 MB', 48, 'muted'),
  stem('07 FX Risers.wav', '48 kHz / 24-bit · 22.4 MB', 57, null),
];

// ---------------------------------------------------------------------------
// 10 Landing page
// ---------------------------------------------------------------------------

/** 140-bar hero waveform, played to 58%. */
export const heroWave = bars(140, 8, 120, 0.58);

/**
 * The design ships each tier's button as raw hex. Reduced here to a tone name
 * so the pricing table draws from the same tokens as every other control —
 * `accent` is the tier the account is already on, `neutral` is a live upgrade,
 * `muted` is a downgrade you can't action from here.
 */
export type PlanCtaTone = 'accent' | 'neutral' | 'muted';

export interface PlanFixture {
  name: string;
  price: string;
  unit: string;
  /** Vault size, shown in the tier's accent colour. */
  storage: string;
  accent: string;
  cta: string;
  ctaTone: PlanCtaTone;
  /** Draws the accent border + tinted fill that marks the active tier. */
  current?: boolean;
  feats: string[];
}

export const plans: PlanFixture[] = [
  {
    name: 'Free',
    price: '$0',
    unit: '/mo',
    storage: '500 MB',
    accent: signalText.muted,
    cta: 'Current plan',
    ctaTone: 'muted',
    feats: ['1 collaborator', '5 versions per project', 'Web upload only'],
  },
  {
    name: 'Pro',
    price: '$14',
    unit: '/mo',
    storage: '20 GB',
    accent: signalColors.accent,
    cta: 'You are here',
    ctaTone: 'accent',
    current: true,
    feats: [
      'Unlimited collaborators',
      'Unlimited versions',
      'Desktop agent + fast sync',
      'Boards',
    ],
  },
  {
    name: 'Studio',
    price: '$39',
    unit: '/mo',
    storage: '500 GB',
    accent: signalColors.cyan,
    cta: 'Upgrade',
    ctaTone: 'neutral',
    feats: [
      'Everything in Pro',
      'External drive roots',
      'Stream-only client links',
      'Priority transfer lanes',
    ],
  },
];

// ---------------------------------------------------------------------------
// 11 Light mode
// ---------------------------------------------------------------------------

const lightPerson = (
  initials: string,
  tone: keyof typeof signalAvatarTonesLight
) => ({ initials, ...signalAvatarTonesLight[tone] });

const LMK = lightPerson('MK', 'accent');
const LJD = lightPerson('JD', 'cyan');
const LAR = lightPerson('AR', 'green');
const lightMore = (n: number) => lightPerson(`+${n}`, 'neutral');

/**
 * The same six projects as `projects`, re-toned for a light surface. Kept as a
 * separate list rather than derived, because one row differs in copy too —
 * Coastline reads "STEM PACK" here where the dark grid says "LOGIC PRO".
 */
export const lightProjects: ProjectFixture[] = [
  {
    name: 'Nightdrive',
    daw: 'Ableton Live',
    logo: signalDawLogos.Ableton,
    meta: 'ABLETON · 1.84 GB · v14',
    updated: '4 min ago',
    status: 'synced',
    statusLabel: 'SYNCED',
    people: [LMK, LJD, lightMore(2)],
  },
  {
    name: 'Halcyon',
    daw: 'FL Studio',
    logo: signalDawLogos.FLStudio,
    meta: 'FL STUDIO · 986 MB · v7',
    updated: '22 min ago',
    status: 'conflict',
    statusLabel: 'CONFLICT',
    people: [LMK, LAR],
  },
  {
    name: 'Coastline (stems)',
    daw: 'Stems',
    logo: signalDawLogos.LogicPro,
    meta: 'STEM PACK · 704 MB · v3',
    updated: '1 h ago',
    status: 'syncing',
    statusLabel: 'SYNCING',
    people: [LJD],
  },
  {
    name: 'Slow Static',
    daw: 'Pro Tools',
    logo: signalDawLogos.ProTools,
    meta: 'PRO TOOLS · 3.10 GB · v21',
    updated: 'Yesterday',
    status: 'behind',
    statusLabel: 'BEHIND 2',
    people: [LMK, LAR, lightMore(4)],
  },
  {
    name: 'Ferrite',
    daw: 'Cubase',
    logo: signalDawLogos.Cubase,
    meta: 'CUBASE · 1.22 GB · v9',
    updated: '2 days ago',
    status: 'synced',
    statusLabel: 'SYNCED',
    people: [LAR],
  },
  {
    name: 'Room Tone 04',
    daw: 'Reaper',
    logo: signalDawLogos.Reaper,
    meta: 'REAPER · 412 MB · v2',
    updated: '5 days ago',
    status: 'synced',
    statusLabel: 'SYNCED',
    people: [LMK, LJD],
  },
].map((p, i) => ({
  ...p,
  wave: lightBars(44, i * 9 + 2, 44),
})) as ProjectFixture[];

/** Light-surface twin of `playerWave` — same seed, so it's the same take. */
export const lightPlayerWave = lightBars(120, 5, 78, 0.43);

/** Version history, re-toned for light. Same three entries the light detail shows. */
export const lightVersions: VersionFixture[] = [
  {
    ...versions[0],
    dot: signalColorsOnLight.accentFill,
    ring: 'rgba(240,86,11,0.14)',
  },
  {
    ...versions[1],
    dot: signalColorsOnLight.cyan,
    ring: 'rgba(30,136,168,0.13)',
  },
  {
    ...versions[2],
    dot: signalColorsOnLight.green,
    ring: 'rgba(23,158,105,0.13)',
  },
];

// ---------------------------------------------------------------------------
// 14 Sync status set
// ---------------------------------------------------------------------------

export interface SyncStateFixture {
  label: string;
  status: SignalStatusKey;
  glyph: string;
  file: string;
  /** Why the agent put the file in this state. */
  rule: string;
  /** What a commit will actually do about it. */
  action: string;
}

/**
 * The six states a file can be in after a status check, each with the rule that
 * produced it and the consequence of committing. Every one lands on a status
 * token that already existed — the sync vocabulary needed no new colours, which
 * is the point of the screen: these are the same six signals used everywhere
 * else, applied to one file at a time.
 */
export const syncStates: SyncStateFixture[] = [
  {
    label: 'UP TO DATE',
    status: 'synced',
    glyph: '✓',
    file: 'Samples/kick_01.wav',
    rule: 'Full hash match',
    action: 'Nothing to do',
  },
  {
    label: 'NEW',
    status: 'stems',
    glyph: '＋',
    file: 'Renders/rough master v14.wav',
    rule: 'Local file with no cloud entry',
    action: 'Included in next push',
  },
  {
    label: 'LOCAL NEWER',
    status: 'behind',
    glyph: '↑',
    file: 'Nightdrive.als',
    rule: 'Hash mismatch, local mtime newer',
    action: 'Push to create v15',
  },
  {
    label: 'BEHIND',
    status: 'syncing',
    glyph: '↓',
    file: 'Samples/Processed/vox_bus.wav',
    rule: 'Hash mismatch, local mtime older',
    action: 'Pull to overwrite local',
  },
  {
    label: 'DELETED LOCALLY',
    status: 'conflict',
    glyph: '−',
    file: 'Samples/Imported/old_loop.wav',
    rule: 'Cloud file with no local file',
    action: 'Push removes it from v15',
  },
  {
    label: 'UNKNOWN',
    status: 'neutral',
    glyph: '?',
    file: 'Ableton Project Info/prefs.cfg',
    rule: 'Ambiguous — clock skew or metadata',
    action: 'Ask before touching',
  },
];

// ---------------------------------------------------------------------------
// 16 Project dialog — plugins detected
// ---------------------------------------------------------------------------

export interface PluginFixture {
  name: string;
  vendor: string;
  /** 0..1 match certainty, rendered as a percentage. */
  confidence: number;
}

export interface PluginGroupFixture {
  type: string;
  status: SignalStatusKey;
  items: PluginFixture[];
}

/**
 * A confident match is green, a probable one amber, and a guess stays muted —
 * the same three-step read the sync states use. Derived rather than stored so
 * the thresholds live in one place.
 */
export const pluginConfidenceColor = (confidence: number): string =>
  confidence >= 0.8
    ? signalColors.green
    : confidence >= 0.5
      ? signalColors.amber
      : signalText.muted;

const plugin = (
  name: string,
  vendor: string,
  confidence: number
): PluginFixture => ({ name, vendor, confidence });

export const pluginGroups: PluginGroupFixture[] = [
  {
    type: 'VST3',
    status: 'syncing',
    items: [
      plugin('Serum 2', 'Xfer Records', 0.98),
      plugin('Pro-Q 4', 'FabFilter', 0.96),
      plugin('Valhalla VintageVerb', 'Valhalla DSP', 0.91),
      plugin('Soothe2', 'oeksound', 0.86),
    ],
  },
  {
    type: 'AU',
    status: 'synced',
    items: [
      plugin('Tape', 'Chow DSP', 0.74),
      plugin('OTT', 'Xfer Records', 0.62),
    ],
  },
  {
    type: 'VST2',
    status: 'stems',
    items: [plugin('Kontakt 6', 'Native Instruments', 0.55)],
  },
  {
    type: 'UNKNOWN',
    status: 'neutral',
    items: [plugin('AUpitch', '—', 0.31), plugin('bx_console', '—', 0.24)],
  },
];

export { MK, JD, AR, more, LMK, LJD, LAR, signalStatus };
